import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { appointmentAPI, serviceAPI, staffAPI } from '../lib/api';
import { formatShortDate, getStatusLabel, getStatusColor } from '../lib/utils';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, startOfWeek, endOfWeek, addMonths, subMonths, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import {
  Plus,
  Edit,
  Trash2,
  Calendar as CalendarIcon,
  Clock,
  User,
  UserPlus,
  Search,
  Phone,
  Mail,
  X,
  MoreVertical,
  MessageSquare,
  Bell,
  UserX,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Filter,
  List,
  LayoutGrid,
  GripVertical,
  Move
} from 'lucide-react';

const appointmentStatuses = [
  { value: 'all', label: 'Tutti gli stati' },
  { value: 'PENDING', label: 'In attesa' },
  { value: 'CONFIRMED', label: 'Confermato' },
  { value: 'COMPLETED', label: 'Completato' },
  { value: 'CANCELLED', label: 'Cancellato' },
  { value: 'NO_SHOW', label: 'Non presentato' }
];

const ITEMS_PER_PAGE = 20;

// Time slots generation
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 8; hour <= 20; hour++) {
    for (let min = 0; min < 60; min += 30) {
      const h = hour.toString().padStart(2, '0');
      const m = min.toString().padStart(2, '0');
      slots.push(`${h}:${m}`);
    }
  }
  return slots;
};

const timeSlots = generateTimeSlots();

// Form component
function AppointmentForm({ 
  formData, 
  setFormData, 
  searchQuery, 
  handleSearchChange, 
  searchResults, 
  selectCustomer,
  setSearchQuery,
  services,
  staffList
}) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Tipo Cliente</Label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant={formData.customer_type === 'existing' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFormData(prev => ({ ...prev, customer_type: 'existing', guest_name: '', guest_phone: '', guest_email: '' }))}
            className="flex-1"
          >
            <User className="w-4 h-4 mr-2" />
            Registrato
          </Button>
          <Button
            type="button"
            variant={formData.customer_type === 'new' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFormData(prev => ({ ...prev, customer_type: 'new', user_id: '' }))}
            className="flex-1"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Non Registrato
          </Button>
        </div>
      </div>

      {formData.customer_type === 'existing' && (
        <div className="space-y-2">
          <Label>Cerca Cliente</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Cerca per nome, email o telefono..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-10"
            />
            {searchResults.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto">
                {searchResults.map(customer => (
                  <div
                    key={customer.id}
                    className="p-3 hover:bg-muted cursor-pointer border-b last:border-b-0"
                    onClick={() => selectCustomer(customer)}
                  >
                    <p className="font-medium">{customer.name}</p>
                    <p className="text-sm text-muted-foreground">{customer.email}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          {formData.user_id && (
            <div className="flex items-center gap-2 p-2 bg-primary/10 rounded-md">
              <User className="w-4 h-4 text-primary" />
              <span className="text-sm">{formData.guest_name}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="ml-auto h-6 w-6 p-0"
                onClick={() => {
                  setFormData(prev => ({ ...prev, user_id: '', guest_name: '' }));
                  setSearchQuery('');
                }}
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          )}
        </div>
      )}

      {formData.customer_type === 'new' && (
        <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
          <div className="space-y-2">
            <Label>Nome Cliente *</Label>
            <Input
              placeholder="Nome e cognome"
              value={formData.guest_name}
              onChange={(e) => setFormData(prev => ({ ...prev, guest_name: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Telefono</Label>
              <Input
                placeholder="+39 333..."
                value={formData.guest_phone}
                onChange={(e) => setFormData(prev => ({ ...prev, guest_phone: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="email@esempio.it"
                value={formData.guest_email}
                onChange={(e) => setFormData(prev => ({ ...prev, guest_email: e.target.value }))}
              />
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label>Servizio *</Label>
        <Select
          value={formData.service_id}
          onValueChange={(v) => setFormData(prev => ({ ...prev, service_id: v }))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Seleziona servizio" />
          </SelectTrigger>
          <SelectContent>
            {services.map(service => (
              <SelectItem key={service.id} value={service.id}>
                {service.name} - €{service.price}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Operatore</Label>
        <Select
          value={formData.staff_id || "any"}
          onValueChange={(v) => setFormData(prev => ({ ...prev, staff_id: v === "any" ? "" : v }))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Qualsiasi operatore" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Qualsiasi operatore</SelectItem>
            {staffList.map(member => (
              <SelectItem key={member.id} value={member.id}>
                {member.user?.name || 'Operatore'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Data *</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {formData.date ? format(formData.date, 'dd/MM/yyyy', { locale: it }) : 'Seleziona'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={formData.date}
                onSelect={(date) => setFormData(prev => ({ ...prev, date: date || new Date() }))}
                locale={it}
              />
            </PopoverContent>
          </Popover>
        </div>
        <div className="space-y-2">
          <Label>Ora *</Label>
          <Select
            value={formData.time}
            onValueChange={(v) => setFormData(prev => ({ ...prev, time: v }))}
          >
            <SelectTrigger>
              <Clock className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-48">
              {timeSlots.map(slot => (
                <SelectItem key={slot} value={slot}>{slot}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Note</Label>
        <Textarea
          placeholder="Note aggiuntive..."
          value={formData.notes}
          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          rows={2}
        />
      </div>
    </div>
  );
}

// Calendar View Component with Drag & Drop
function CalendarView({ appointments, currentMonth, setCurrentMonth, selectedDate, setSelectedDate, onDateClick, onAppointmentDrop }) {
  const [draggedAppointment, setDraggedAppointment] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  const weekDays = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

  const getAppointmentsForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return appointments.filter(apt => apt.date === dateStr);
  };

  const getStatusDot = (status) => {
    switch (status) {
      case 'PENDING': return 'bg-yellow-500';
      case 'CONFIRMED': return 'bg-blue-500';
      case 'COMPLETED': return 'bg-green-500';
      case 'CANCELLED': return 'bg-gray-400';
      case 'NO_SHOW': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  // Drag handlers
  const handleDragStart = (e, apt) => {
    setDraggedAppointment(apt);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', apt.id);
    // Add a slight delay to ensure the drag image is set
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedAppointment(null);
    setDragOverDate(null);
  };

  const handleDragOver = (e, day) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDate(day);
  };

  const handleDragLeave = () => {
    setDragOverDate(null);
  };

  const handleDrop = (e, targetDay) => {
    e.preventDefault();
    setDragOverDate(null);
    
    if (draggedAppointment) {
      const newDate = format(targetDay, 'yyyy-MM-dd');
      const oldDate = draggedAppointment.date;
      
      // Only update if the date has changed
      if (newDate !== oldDate) {
        onAppointmentDrop(draggedAppointment.id, newDate, draggedAppointment.time);
      }
    }
    setDraggedAppointment(null);
  };

  return (
    <div className="bg-card rounded-lg border p-4">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <h3 className="font-semibold text-lg">
          {format(currentMonth, 'MMMM yyyy', { locale: it })}
        </h3>
        <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Drag instruction */}
      <div className="flex items-center gap-2 mb-3 px-2 py-1.5 bg-muted/50 rounded-md text-xs text-muted-foreground">
        <Move className="w-3 h-3" />
        <span>Trascina gli appuntamenti per spostarli in un altro giorno</span>
      </div>

      {/* Week days header */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekDays.map(day => (
          <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar days */}
      <div className="grid grid-cols-7 gap-1">
        {days.map(day => {
          const dayAppointments = getAppointmentsForDay(day);
          const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
          const isSelected = selectedDate && isSameDay(day, selectedDate);
          const isTodayDate = isToday(day);
          const isDragOver = dragOverDate && isSameDay(day, dragOverDate);

          return (
            <div
              key={day.toISOString()}
              className={`
                min-h-[80px] p-1 rounded-lg border cursor-pointer transition-all
                ${!isCurrentMonth ? 'bg-muted/30 text-muted-foreground' : 'bg-background'}
                ${isSelected ? 'ring-2 ring-primary border-primary' : 'hover:bg-muted/50'}
                ${isTodayDate ? 'border-primary' : ''}
                ${isDragOver ? 'ring-2 ring-green-500 bg-green-50 dark:bg-green-950/30' : ''}
              `}
              onClick={() => onDateClick(day)}
              onDragOver={(e) => handleDragOver(e, day)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, day)}
            >
              <div className={`text-sm font-medium mb-1 ${isTodayDate ? 'text-primary' : ''}`}>
                {format(day, 'd')}
              </div>
              <div className="space-y-0.5">
                {dayAppointments.slice(0, 3).map(apt => (
                  <div
                    key={apt.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, apt)}
                    onDragEnd={handleDragEnd}
                    className={`
                      flex items-center gap-1 text-xs truncate rounded px-1 py-0.5
                      cursor-grab active:cursor-grabbing hover:bg-muted
                      ${draggedAppointment?.id === apt.id ? 'opacity-50' : ''}
                    `}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <GripVertical className="w-2 h-2 text-muted-foreground flex-shrink-0" />
                    <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${getStatusDot(apt.status)}`} />
                    <span className="truncate">{apt.time}</span>
                  </div>
                ))}
                {dayAppointments.length > 3 && (
                  <div className="text-xs text-muted-foreground pl-1">
                    +{dayAppointments.length - 3} altri
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t text-xs">
        <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-yellow-500" /> In attesa</div>
        <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-blue-500" /> Confermato</div>
        <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-green-500" /> Completato</div>
        <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-red-500" /> Non presentato</div>
      </div>
    </div>
  );
}

// Day Schedule View - Grid with time slots for drag & drop
function DayScheduleView({ appointments, selectedDate, onAppointmentDrop, staffList, services }) {
  const [draggedAppointment, setDraggedAppointment] = useState(null);
  const [dragOverSlot, setDragOverSlot] = useState(null);
  
  // Generate time slots from 08:00 to 20:00
  const timeSlots = [];
  for (let hour = 8; hour <= 20; hour++) {
    timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
    timeSlots.push(`${hour.toString().padStart(2, '0')}:30`);
  }

  const dateStr = format(selectedDate, 'yyyy-MM-dd');
  const dayAppointments = appointments.filter(apt => apt.date === dateStr);

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'bg-yellow-100 border-yellow-400 text-yellow-800';
      case 'CONFIRMED': return 'bg-blue-100 border-blue-400 text-blue-800';
      case 'COMPLETED': return 'bg-green-100 border-green-400 text-green-800';
      case 'CANCELLED': return 'bg-gray-100 border-gray-400 text-gray-600';
      case 'NO_SHOW': return 'bg-red-100 border-red-400 text-red-800';
      default: return 'bg-gray-100 border-gray-400 text-gray-600';
    }
  };

  const getAppointmentAtSlot = (time) => {
    return dayAppointments.find(apt => apt.time === time);
  };

  const getServiceName = (serviceId) => {
    const service = services?.find(s => s.id === serviceId);
    return service?.name || 'Servizio';
  };

  const getStaffName = (staffId) => {
    const staff = staffList?.find(s => s.id === staffId);
    return staff?.user?.name || 'Operatore';
  };

  const getCustomerName = (apt) => {
    return apt.guest_name || apt.user?.name || 'Cliente';
  };

  // Drag handlers
  const handleDragStart = (e, apt) => {
    setDraggedAppointment(apt);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', apt.id);
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedAppointment(null);
    setDragOverSlot(null);
  };

  const handleDragOver = (e, time) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverSlot(time);
  };

  const handleDragLeave = () => {
    setDragOverSlot(null);
  };

  const handleDrop = (e, targetTime) => {
    e.preventDefault();
    setDragOverSlot(null);
    
    if (draggedAppointment && draggedAppointment.time !== targetTime) {
      onAppointmentDrop(draggedAppointment.id, dateStr, targetTime);
    }
    setDraggedAppointment(null);
  };

  return (
    <div className="bg-card rounded-lg border p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-lg">
          {format(selectedDate, 'EEEE d MMMM yyyy', { locale: it })}
        </h3>
        <Badge variant="outline">{dayAppointments.length} appuntamenti</Badge>
      </div>

      {/* Drag instruction */}
      <div className="flex items-center gap-2 mb-3 px-2 py-1.5 bg-muted/50 rounded-md text-xs text-muted-foreground">
        <Move className="w-3 h-3" />
        <span>Trascina un appuntamento su un altro orario per spostarlo</span>
      </div>

      {/* Time Grid */}
      <div className="space-y-1 max-h-[500px] overflow-y-auto">
        {timeSlots.map((time) => {
          const apt = getAppointmentAtSlot(time);
          const isCurrentSlotDragOver = dragOverSlot === time;
          const isOccupied = !!apt;

          return (
            <div
              key={time}
              className={`
                flex items-stretch border rounded-lg transition-all min-h-[48px]
                ${isCurrentSlotDragOver && !isOccupied ? 'ring-2 ring-primary bg-primary/10' : ''}
                ${!isOccupied ? 'hover:bg-muted/30' : ''}
              `}
              onDragOver={(e) => !isOccupied && handleDragOver(e, time)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => !isOccupied && handleDrop(e, time)}
            >
              {/* Time Label */}
              <div className="w-16 flex-shrink-0 flex items-center justify-center text-sm font-medium text-muted-foreground border-r bg-muted/20">
                {time}
              </div>

              {/* Slot Content */}
              <div className="flex-1 p-1">
                {apt ? (
                  <div
                    draggable
                    onDragStart={(e) => handleDragStart(e, apt)}
                    onDragEnd={handleDragEnd}
                    className={`
                      flex items-center gap-2 p-2 rounded border-l-4 cursor-grab active:cursor-grabbing
                      ${getStatusColor(apt.status)}
                      ${draggedAppointment?.id === apt.id ? 'opacity-50' : ''}
                    `}
                  >
                    <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{getCustomerName(apt)}</p>
                      <p className="text-xs truncate opacity-75">
                        {getServiceName(apt.service_id)} • {getStaffName(apt.staff_id)}
                      </p>
                    </div>
                    <Clock className="w-3 h-3 opacity-50" />
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-xs text-muted-foreground/50">
                    {isCurrentSlotDragOver ? 'Rilascia qui' : ''}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t text-xs">
        <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-yellow-400" /> In attesa</div>
        <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-blue-400" /> Confermato</div>
        <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-green-400" /> Completato</div>
        <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-red-400" /> Non presentato</div>
      </div>
    </div>
  );
}

// Pagination Component
function Pagination({ currentPage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  const pages = [];
  const maxVisible = 5;
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  
  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1);
  }

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  return (
    <div className="flex items-center justify-center gap-1 mt-4">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
      
      {start > 1 && (
        <>
          <Button variant="outline" size="sm" onClick={() => onPageChange(1)}>1</Button>
          {start > 2 && <span className="px-2">...</span>}
        </>
      )}
      
      {pages.map(page => (
        <Button
          key={page}
          variant={page === currentPage ? 'default' : 'outline'}
          size="sm"
          onClick={() => onPageChange(page)}
        >
          {page}
        </Button>
      ))}
      
      {end < totalPages && (
        <>
          {end < totalPages - 1 && <span className="px-2">...</span>}
          <Button variant="outline" size="sm" onClick={() => onPageChange(totalPages)}>{totalPages}</Button>
        </>
      )}
      
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  );
}

export default function AppointmentManager({ appointments, onRefresh }) {
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isNoShowOpen, setIsNoShowOpen] = useState(false);
  const [isReminderOpen, setIsReminderOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reminderChannel, setReminderChannel] = useState('sms');
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [staffFilter, setStaffFilter] = useState('all');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  
  // Services and staff for dropdowns
  const [services, setServices] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    customer_type: 'existing',
    user_id: '',
    guest_name: '',
    guest_phone: '',
    guest_email: '',
    service_id: '',
    staff_id: '',
    date: new Date(),
    time: '09:00',
    notes: ''
  });

  useEffect(() => {
    loadServices();
    loadStaff();
  }, []);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, staffFilter, selectedDate]);

  const loadServices = async () => {
    try {
      const data = await serviceAPI.list();
      setServices(data.data || data || []);
    } catch (error) {
      console.error('Error loading services:', error);
    }
  };

  const loadStaff = async () => {
    try {
      const data = await staffAPI.list();
      setStaffList(data.data || data || []);
    } catch (error) {
      console.error('Error loading staff:', error);
    }
  };

  // Filter appointments
  const filteredAppointments = useMemo(() => {
    return appointments.filter(apt => {
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const customerName = (apt.guest_name || apt.user?.name || '').toLowerCase();
        const customerPhone = (apt.guest_phone || apt.user?.phone || '').toLowerCase();
        if (!customerName.includes(search) && !customerPhone.includes(search)) {
          return false;
        }
      }
      
      // Status filter
      if (statusFilter !== 'all' && apt.status !== statusFilter) {
        return false;
      }
      
      // Staff filter
      if (staffFilter !== 'all' && apt.staff_id !== staffFilter) {
        return false;
      }
      
      // Date filter (from calendar)
      if (selectedDate) {
        const aptDate = apt.date;
        const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
        if (aptDate !== selectedDateStr) {
          return false;
        }
      }
      
      return true;
    });
  }, [appointments, searchTerm, statusFilter, staffFilter, selectedDate]);

  // Paginated appointments
  const paginatedAppointments = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredAppointments.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredAppointments, currentPage]);

  const totalPages = Math.ceil(filteredAppointments.length / ITEMS_PER_PAGE);

  const searchCustomers = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/search?q=${encodeURIComponent(query)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Store-Slug': localStorage.getItem('storeSlug')
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
      }
    } catch (error) {
      console.error('Error searching customers:', error);
    }
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    searchCustomers(value);
  };

  const selectCustomer = (customer) => {
    setFormData(prev => ({
      ...prev,
      user_id: customer.id,
      guest_name: customer.name,
      guest_phone: customer.phone || '',
      guest_email: customer.email
    }));
    setSearchQuery(customer.name);
    setSearchResults([]);
  };

  const resetForm = () => {
    setFormData({
      customer_type: 'existing',
      user_id: '',
      guest_name: '',
      guest_phone: '',
      guest_email: '',
      service_id: '',
      staff_id: '',
      date: selectedDate || new Date(),
      time: '09:00',
      notes: ''
    });
    setSearchQuery('');
    setSearchResults([]);
  };

  const openCreate = () => {
    resetForm();
    setIsCreateOpen(true);
  };

  const openEdit = (appointment) => {
    setSelectedAppointment(appointment);
    setFormData({
      customer_type: appointment.user_id ? 'existing' : 'new',
      user_id: appointment.user_id || '',
      guest_name: appointment.guest_name || appointment.user?.name || '',
      guest_phone: appointment.guest_phone || '',
      guest_email: appointment.guest_email || '',
      service_id: appointment.service_id || '',
      staff_id: appointment.staff_id || '',
      date: new Date(appointment.date),
      time: appointment.time || '09:00',
      notes: appointment.notes || ''
    });
    setSearchQuery(appointment.guest_name || appointment.user?.name || '');
    setIsEditOpen(true);
  };

  const openDelete = (appointment) => {
    setSelectedAppointment(appointment);
    setIsDeleteOpen(true);
  };

  const openNoShow = (appointment) => {
    setSelectedAppointment(appointment);
    setIsNoShowOpen(true);
  };

  const openReminder = (appointment) => {
    setSelectedAppointment(appointment);
    setReminderChannel('sms');
    setIsReminderOpen(true);
  };

  const getPhoneNumber = (appointment) => {
    return appointment.guest_phone || appointment.user?.phone;
  };

  const handleCall = (appointment) => {
    const phone = getPhoneNumber(appointment);
    if (phone) {
      window.location.href = `tel:${phone}`;
    } else {
      toast.error('Nessun numero di telefono disponibile');
    }
  };

  const handleSendReminder = async () => {
    if (!selectedAppointment) return;
    
    const phone = getPhoneNumber(selectedAppointment);
    if (!phone) {
      toast.error('Nessun numero di telefono disponibile');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/appointments/${selectedAppointment.id}/send-reminder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Store-Slug': localStorage.getItem('storeSlug'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ channel: reminderChannel })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Sollecito inviato via ${reminderChannel.toUpperCase()}${data.is_mock ? ' (MOCK)' : ''}`);
        setIsReminderOpen(false);
        onRefresh();
      } else {
        toast.error(data.error || "Errore nell'invio del sollecito");
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error("Errore nell'invio del sollecito");
    } finally {
      setLoading(false);
    }
  };

  const handleNoShow = async () => {
    if (!selectedAppointment) return;

    setLoading(true);
    try {
      await appointmentAPI.updateStatus(selectedAppointment.id, 'NO_SHOW');
      
      const hasPaidOnline = selectedAppointment.payment_info?.paid_online;
      if (hasPaidOnline) {
        toast.success('Cliente segnato come non presentato. Refund del 90% processato.');
      } else {
        toast.success('Cliente segnato come non presentato.');
      }
      
      setIsNoShowOpen(false);
      onRefresh();
    } catch (error) {
      console.error('Error marking no-show:', error);
      toast.error('Errore nel segnare il cliente come non presentato');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.service_id || !formData.date || !formData.time) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    if (formData.customer_type === 'new' && !formData.guest_name) {
      toast.error('Inserisci il nome del cliente');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        service_id: formData.service_id,
        staff_id: formData.staff_id || null,
        date: format(formData.date, 'yyyy-MM-dd'),
        time: formData.time,
        notes: formData.notes || null
      };

      if (formData.customer_type === 'existing' && formData.user_id) {
        payload.user_id = formData.user_id;
      } else {
        payload.guest_name = formData.guest_name;
        payload.guest_phone = formData.guest_phone || null;
        payload.guest_email = formData.guest_email || null;
      }

      await appointmentAPI.create(payload);
      toast.success('Appuntamento creato con successo');
      setIsCreateOpen(false);
      resetForm();
      onRefresh();
    } catch (error) {
      console.error('Error creating appointment:', error);
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedAppointment) return;

    setLoading(true);
    try {
      const payload = {
        service_id: formData.service_id,
        staff_id: formData.staff_id || null,
        date: format(formData.date, 'yyyy-MM-dd'),
        time: formData.time,
        notes: formData.notes || null
      };

      if (formData.customer_type === 'existing' && formData.user_id) {
        payload.user_id = formData.user_id;
      } else {
        payload.guest_name = formData.guest_name;
        payload.guest_phone = formData.guest_phone || null;
        payload.guest_email = formData.guest_email || null;
      }

      await appointmentAPI.update(selectedAppointment.id, payload);
      toast.success('Appuntamento aggiornato');
      setIsEditOpen(false);
      onRefresh();
    } catch (error) {
      console.error('Error updating appointment:', error);
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    if (newStatus === 'NO_SHOW') {
      const apt = appointments.find(a => a.id === appointmentId);
      if (apt) {
        openNoShow(apt);
        return;
      }
    }
    
    try {
      await appointmentAPI.updateStatus(appointmentId, newStatus);
      toast.success('Stato aggiornato');
      onRefresh();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error("Errore nell'aggiornamento dello stato");
    }
  };

  const handleDelete = async () => {
    if (!selectedAppointment) return;

    setLoading(true);
    try {
      await appointmentAPI.delete(selectedAppointment.id);
      toast.success('Appuntamento eliminato');
      setIsDeleteOpen(false);
      setSelectedAppointment(null);
      onRefresh();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    } finally {
      setLoading(false);
    }
  };

  const handleDateClick = (date) => {
    if (selectedDate && isSameDay(date, selectedDate)) {
      setSelectedDate(null); // Deselect if same date
    } else {
      setSelectedDate(date);
    }
  };

  // Handle drag & drop appointment reschedule
  const handleAppointmentDrop = async (appointmentId, newDate, time) => {
    try {
      const appointment = appointments.find(apt => apt.id === appointmentId);
      if (!appointment) return;

      // Update the appointment with the new date
      await appointmentAPI.update(appointmentId, {
        service_id: appointment.service_id,
        staff_id: appointment.staff_id || null,
        date: newDate,
        time: time,
        notes: appointment.notes || null,
        user_id: appointment.user_id || null,
        guest_name: appointment.guest_name || null,
        guest_phone: appointment.guest_phone || null,
        guest_email: appointment.guest_email || null
      });

      toast.success(`Appuntamento spostato al ${format(parseISO(newDate), 'dd/MM/yyyy', { locale: it })}`);
      onRefresh();
    } catch (error) {
      console.error('Error moving appointment:', error);
      toast.error("Errore nello spostamento dell'appuntamento");
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setStaffFilter('all');
    setSelectedDate(null);
    setCurrentPage(1);
  };

  const hasActiveFilters = searchTerm || statusFilter !== 'all' || staffFilter !== 'all' || selectedDate;

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <CardTitle>Gestione Appuntamenti</CardTitle>
          <Badge variant="secondary">{filteredAppointments.length} risultati</Badge>
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex border rounded-md">
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              className="rounded-r-none"
              onClick={() => setViewMode('list')}
            >
              <List className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'calendar' ? 'secondary' : 'ghost'}
              size="sm"
              className="rounded-l-none"
              onClick={() => setViewMode('calendar')}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </div>
          <Button onClick={openCreate} data-testid="create-appointment-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuovo
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4 p-4 bg-muted/50 rounded-lg">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Cerca cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Stato" />
            </SelectTrigger>
            <SelectContent>
              {appointmentStatuses.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={staffFilter} onValueChange={setStaffFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Operatore" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli operatori</SelectItem>
              {staffList.map(member => (
                <SelectItem key={member.id} value={member.id}>
                  {member.user?.name || 'Operatore'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selectedDate && (
            <Badge variant="secondary" className="flex items-center gap-1 h-10 px-3">
              <CalendarIcon className="w-3 h-3" />
              {format(selectedDate, 'dd/MM/yyyy', { locale: it })}
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0 ml-1"
                onClick={() => setSelectedDate(null)}
              >
                <X className="w-3 h-3" />
              </Button>
            </Badge>
          )}

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="w-4 h-4 mr-1" />
              Cancella filtri
            </Button>
          )}
        </div>

        {viewMode === 'calendar' ? (
          <div className="grid lg:grid-cols-[1fr_400px] gap-4">
            <CalendarView
              appointments={appointments}
              currentMonth={currentMonth}
              setCurrentMonth={setCurrentMonth}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              onDateClick={handleDateClick}
              onAppointmentDrop={handleAppointmentDrop}
            />
            
            {/* Day Schedule View with Time Slots */}
            {selectedDate ? (
              <DayScheduleView
                appointments={appointments}
                selectedDate={selectedDate}
                onAppointmentDrop={handleAppointmentDrop}
                staffList={staffList}
                services={services}
              />
            ) : (
              <div className="bg-muted/30 rounded-lg p-4 flex items-center justify-center min-h-[400px]">
                <div className="text-center text-muted-foreground">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="font-medium">Seleziona una data</p>
                  <p className="text-sm">Clicca su un giorno per gestire gli appuntamenti</p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {paginatedAppointments.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                {hasActiveFilters ? 'Nessun appuntamento trovato con i filtri selezionati' : 'Nessun appuntamento'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table w-full">
                  <thead>
                    <tr>
                      <th className="text-left p-3">Data</th>
                      <th className="text-left p-3">Ora</th>
                      <th className="text-left p-3">Servizio</th>
                      <th className="text-left p-3">Cliente</th>
                      <th className="text-left p-3">Operatore</th>
                      <th className="text-left p-3">Stato</th>
                      <th className="text-left p-3">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedAppointments.map(apt => (
                      <tr key={apt.id} className="border-b">
                        <td className="p-3">{formatShortDate(apt.date)}</td>
                        <td className="p-3">{apt.time}</td>
                        <td className="p-3">{apt.service?.name || '-'}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div>
                              <p className="font-medium">{apt.guest_name || apt.user?.name || 'Cliente'}</p>
                              {getPhoneNumber(apt) && (
                                <p className="text-xs text-muted-foreground">{getPhoneNumber(apt)}</p>
                              )}
                            </div>
                            {getPhoneNumber(apt) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => handleCall(apt)}
                                title="Chiama cliente"
                              >
                                <Phone className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                        <td className="p-3">{apt.staff?.user?.name || 'Non assegnato'}</td>
                        <td className="p-3">
                          <Select
                            value={apt.status}
                            onValueChange={(v) => handleStatusChange(apt.id, v)}
                          >
                            <SelectTrigger className="w-36">
                              <Badge className={`status-badge ${getStatusColor(apt.status)}`}>
                                {getStatusLabel(apt.status)}
                              </Badge>
                            </SelectTrigger>
                            <SelectContent>
                              {appointmentStatuses.filter(s => s.value !== 'all').map(s => (
                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-3">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEdit(apt)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Modifica
                              </DropdownMenuItem>
                              {getPhoneNumber(apt) && (
                                <>
                                  <DropdownMenuItem onClick={() => handleCall(apt)}>
                                    <Phone className="w-4 h-4 mr-2 text-green-600" />
                                    Chiama
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => openReminder(apt)}>
                                    <Bell className="w-4 h-4 mr-2 text-orange-500" />
                                    Sollecito
                                  </DropdownMenuItem>
                                </>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => openNoShow(apt)} className="text-amber-600">
                                <UserX className="w-4 h-4 mr-2" />
                                Non presentato
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openDelete(apt)} className="text-destructive">
                                <Trash2 className="w-4 h-4 mr-2" />
                                Elimina
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </CardContent>

      {/* Dialogs */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuovo Appuntamento</DialogTitle>
            <DialogDescription>Crea un nuovo appuntamento</DialogDescription>
          </DialogHeader>
          <AppointmentForm
            formData={formData}
            setFormData={setFormData}
            searchQuery={searchQuery}
            handleSearchChange={handleSearchChange}
            searchResults={searchResults}
            selectCustomer={selectCustomer}
            setSearchQuery={setSearchQuery}
            services={services}
            staffList={staffList}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Annulla</Button>
            <Button onClick={handleCreate} disabled={loading}>
              {loading ? 'Creazione...' : 'Crea'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifica Appuntamento</DialogTitle>
          </DialogHeader>
          <AppointmentForm
            formData={formData}
            setFormData={setFormData}
            searchQuery={searchQuery}
            handleSearchChange={handleSearchChange}
            searchResults={searchResults}
            selectCustomer={selectCustomer}
            setSearchQuery={setSearchQuery}
            services={services}
            staffList={staffList}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>Annulla</Button>
            <Button onClick={handleUpdate} disabled={loading}>
              {loading ? 'Salvataggio...' : 'Salva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma Eliminazione</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare questo appuntamento?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>Annulla</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={loading}>
              {loading ? 'Eliminazione...' : 'Elimina'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isNoShowOpen} onOpenChange={setIsNoShowOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <UserX className="w-5 h-5" />
              Cliente Non Presentato
            </DialogTitle>
            <DialogDescription asChild>
              <div>
                <p className="mb-3">Confermi che il cliente non si è presentato?</p>
                {selectedAppointment?.payment_info?.paid_online ? (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      <p className="text-sm text-amber-700">
                        Verrà processato un rimborso del <strong>90%</strong> (10% trattenuto come penale).
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Nessun pagamento online da rimborsare.</p>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNoShowOpen(false)}>Annulla</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={handleNoShow} disabled={loading}>
              {loading ? 'Elaborazione...' : 'Conferma'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isReminderOpen} onOpenChange={setIsReminderOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-orange-500" />
              Invia Sollecito
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Button
                variant={reminderChannel === 'sms' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setReminderChannel('sms')}
                className="flex-1"
              >
                SMS
              </Button>
              <Button
                variant={reminderChannel === 'whatsapp' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setReminderChannel('whatsapp')}
                className="flex-1"
              >
                WhatsApp
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReminderOpen(false)}>Annulla</Button>
            <Button onClick={handleSendReminder} disabled={loading}>
              {loading ? 'Invio...' : `Invia ${reminderChannel.toUpperCase()}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
